export interface TTSOptions {
  text: string;
  language: string;
  rate?: number;
  pitch?: number;
  volume?: number;
}

export class TextToSpeechService {
  private synth: SpeechSynthesis;
  private isSupported: boolean;

  constructor() {
    const windowWithSpeechSynthesis = window as any;
    this.synth =
      windowWithSpeechSynthesis.speechSynthesis ||
      windowWithSpeechSynthesis.webkitSpeechSynthesis;
    this.isSupported = !!this.synth;
  }

  async generateSpeech(options: TTSOptions): Promise<Blob> {
    if (!this.isSupported) {
      throw new Error('Speech Synthesis not supported');
    }

    return new Promise((resolve, reject) => {
      const utterance = new SpeechSynthesisUtterance(options.text);
      utterance.lang = this.getLanguageCode(options.language);
      utterance.rate = options.rate || 1;
      utterance.pitch = options.pitch || 1;
      utterance.volume = options.volume || 1;

      const chunks: Uint8Array[] = [];
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const mediaStreamAudioDestination = audioContext.createMediaStreamDestination();
      const mediaRecorder = new MediaRecorder(mediaStreamAudioDestination.stream);

      mediaRecorder.ondataavailable = (event) => {
        chunks.push(new Uint8Array(event.data));
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/wav' });
        resolve(blob);
      };

      utterance.onend = () => {
        mediaRecorder.stop();
      };

      utterance.onerror = (event) => {
        reject(new Error(`Speech synthesis error: ${event.error}`));
      };

      mediaRecorder.start();
      this.synth.speak(utterance);
    });
  }

  private getLanguageCode(language: string): string {
    const languageCodes: Record<string, string> = {
      ar: 'ar-SA',
      en: 'en-US',
      fr: 'fr-FR',
      es: 'es-ES',
    };
    return languageCodes[language] || 'en-US';
  }

  speak(text: string, language: string = 'ar'): Promise<void> {
    if (!this.isSupported) {
      return Promise.reject(new Error('Speech Synthesis not supported'));
    }

    return new Promise((resolve, reject) => {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = this.getLanguageCode(language);
      utterance.onend = () => resolve();
      utterance.onerror = (event) =>
        reject(new Error(`Speech error: ${event.error}`));

      this.synth.speak(utterance);
    });
  }

  cancel(): void {
    if (this.isSupported) {
      this.synth.cancel();
    }
  }
}

export const ttsService = new TextToSpeechService();
